import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { fetchGallery } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Camera, Calendar, Image as ImageIcon, ArrowRight } from 'lucide-react';
import { format, parseISO } from 'date-fns';

// Constants for image handling
const BASE_URL = `https://api.ibadansouthanglicandiocese.org.ng/`;
const DEFAULT_LOGO = `${BASE_URL}logos/1741869083_site_icon.png`;

/**
 * Helper component to handle conditional image sourcing and broken links
 */
const AlbumThumbnail = ({ src, alt, className = "" }) => {
    const getFullSrc = () => {
        if (!src) return DEFAULT_LOGO;
        if (src.startsWith('http')) return src;
        return `${BASE_URL}${src}`;
    };

    return (
        <img
            src={getFullSrc()}
            alt={alt || "Gallery thumbnail"}
            className={className}
            onError={(e) => { (e.target as HTMLImageElement).src = DEFAULT_LOGO; }}
        />
    );
};

interface GalleryAlbum {
    id: number;
    title: string;
    date: string;
    images: number;
    thumbnail: string;
}

const Gallery = () => {
    const [galleryItems, setGalleryItems] = useState<GalleryAlbum[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { toast } = useToast();

    useEffect(() => {
        const getGallery = async () => {
            try {
                const response = await fetchGallery();
                const data = response.data;

                const formatted = data.map((item: any) => ({
                    id: item.id,
                    title: item.title,
                    date: item.date,
                    images: item.images || 0,
                    thumbnail: item.thumbnail,
                }));

                setGalleryItems(formatted);
            } catch (err) {
                console.error(err);
                setError('Failed to load gallery.');
            } finally {
                setLoading(false);
            }
        };

        getGallery();
    }, [toast]);

    return (
        <div className="min-h-screen bg-[#fcfaf7] py-20 px-4">
            <div className="max-w-7xl mx-auto">
                
                {/* Header Section */}
                <header className="max-w-3xl mx-auto text-center mb-20">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="flex justify-center mb-6"
                    >
                        <div className="p-3 bg-amber-100 rounded-2xl text-amber-700">
                            <Camera className="w-8 h-8" />
                        </div>
                    </motion.div>
                    
                    <motion.h1 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-4xl md:text-5xl lg:text-6xl font-serif font-bold text-slate-900 mb-6"
                    >
                        Diocesan Archives
                    </motion.h1>
                    
                    <motion.p 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="text-lg text-slate-600 font-serif leading-relaxed"
                    >
                        Capturing the growth, worship, and fellowship of the Ibadan South Anglican Diocese through the years.
                    </motion.p>
                </header>

                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
                        {[1, 2, 3].map((n) => (
                            <div key={n} className="h-96 bg-slate-200 rounded-[2.5rem] animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
                        {galleryItems.map((album, index) => (
                            <motion.div
                                key={album.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.05 }}
                            >
                                <Link to={`/gallery/${album.id}`} className="group block relative">
                                    {/* Stacked Paper Effect */}
                                    <div className="absolute inset-0 bg-white rounded-[2.5rem] shadow-md translate-x-2 translate-y-2 group-hover:translate-x-3 group-hover:translate-y-3 transition-transform duration-300 border border-slate-100" />
                                    
                                    <Card className="relative z-10 overflow-hidden rounded-[2.5rem] border-none shadow-xl bg-white transition-all duration-500 group-hover:-translate-y-2">
                                        <CardContent className="p-0">
                                            {/* Image with ImageHelper Logic */}
                                            <div className="relative h-64 overflow-hidden">
                                                <AlbumThumbnail 
                                                    src={album.thumbnail} 
                                                    alt={album.title} 
                                                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110 group-hover:brightness-90"
                                                />
                                                
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                                
                                                <div className="absolute bottom-4 left-4">
                                                    <div className="bg-white/90 backdrop-blur-sm text-slate-900 px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-2">
                                                        <ImageIcon className="w-3 h-3 text-amber-600" />
                                                        {album.images} Photos
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="p-8">
                                                <div className="flex items-center gap-2 text-amber-600 mb-3">
                                                    <Calendar className="w-3.5 h-3.5" />
                                                    <span className="text-[10px] font-bold uppercase tracking-[0.2em]">
                                                        {album.date ? format(parseISO(album.date), 'MMMM d, yyyy') : 'Recently Added'}
                                                    </span>
                                                </div>
                                                
                                                <h3 className="text-xl font-serif font-bold text-slate-900 leading-tight group-hover:text-amber-700 transition-colors line-clamp-2">
                                                    {album.title}
                                                </h3>
                                                
                                                <div className="mt-6 flex items-center text-slate-300 group-hover:text-amber-600 transition-colors">
                                                    <span className="text-xs font-bold uppercase tracking-widest opacity-0 group-hover:opacity-100 transition-all -translate-x-2 group-hover:translate-x-0">View Album</span>
                                                    <div className="h-px flex-1 bg-slate-100 group-hover:bg-amber-100 mx-4" />
                                                    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
                                                </div>
                                            </div>
                                        </CardContent>
                                    </Card>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default Gallery;